/**
 * Quote Query Repository
 *
 * Handles all read operations for quotes.
 * This repository is responsible for fetching quote data from the database.
 */

import { BaseRepository, RepositoryResult, PaginatedResult, QueryOptions, PaginationParams } from './BaseRepository';
import { getDbClient } from '@/server/lib/database';
import { Quote, QuoteItem, Client, CurrencyCode } from '@/shared/types';

/**
 * Database Quote entity with relations
 */
interface QuoteWithRelations {
  id: string;
  user_id: string;
  client_id: string;
  quote_number: string;
  title: string;
  description?: string;
  status: 'draft' | 'sent' | 'accepted' | 'rejected';
  valid_until: string;
  subtotal?: number;
  tax_rate?: number;
  tax_amount?: number;
  total_amount: number;
  notes?: string;
  terms?: string;
  public_token?: string;
  created_at: string;
  updated_at?: string;
  client?: {
    id: string;
    name: string;
    email: string;
  };
  quote_items?: Array<{
    id: string;
    service: {
      id: string;
      name: string;
      rate: number;
    };
    quantity: number;
    rate: number;
    total?: number;
    description?: string;
  }>;
}

/**
 * Quote Query Repository
 * Provides read-only operations for the quotes table
 */
export class QuoteQueryRepository extends BaseRepository<Quote> {
  protected tableName = 'quotes' as const;

  /**
   * Find all quotes for a user with pagination and filtering
   */
  async findAll(
    userId: string,
    options?: QueryOptions & PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Quote>>> {
    try {
      const db = getDbClient();
      const { page, limit } = this.parsePagination(options);
      const { from, to } = this.getRange(page, limit);

      let query = db
        .from(this.tableName)
        .select(
          `
          *,
          client:clients(id, name, email),
          quote_items(
            id,
            service:services(id, name, rate),
            quantity,
            rate,
            total,
            description
          )
        `,
          { count: 'exact' }
        )
        .eq('user_id', userId);

      if (options?.filters) {
        query = this.applyFilters(query, options.filters);
      }

      if (options?.orderBy) {
        query = query.order(options.orderBy, { ascending: options.ascending ?? false });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        throw error;
      }

      const quotes = (data || []).map((dbQuote) => this.mapToQuote(dbQuote as QuoteWithRelations));

      const pagination = this.buildPagination(page, limit, count || 0);

      return {
        success: true,
        data: {
          data: quotes,
          pagination,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching quotes'),
      };
    }
  }

  /**
   * Find a single quote by ID with relations
   */
  async findById(id: string): Promise<RepositoryResult<Quote>> {
    try {
      const db = getDbClient();

      const { data, error } = await db
        .from(this.tableName)
        .select(
          `
          *,
          client:clients(id, name, email),
          quote_items(
            id,
            service:services(id, name, rate),
            quantity,
            rate,
            total,
            description
          )
        `
        )
        .eq('id', id)
        .single();

      if (error) {
        throw error;
      }

      if (!data) {
        return {
          success: false,
          error: 'Quote not found',
        };
      }

      const quote = this.mapToQuote(data as QuoteWithRelations);

      return {
        success: true,
        data: quote,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching quote'),
      };
    }
  }

  /**
   * Find quotes by user ID (returns array, not paginated)
   * This is a convenience method that returns all quotes for a user
   */
  async findByUserId(userId: string): Promise<RepositoryResult<Quote[]>> {
    try {
      const db = getDbClient();

      const { data, error } = await db
        .from(this.tableName)
        .select(
          `
          *,
          client:clients(id, name, email),
          quote_items(
            id,
            service:services(id, name, rate),
            quantity,
            rate,
            total,
            description
          )
        `
        )
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      const quotes = (data || []).map((dbQuote) => this.mapToQuote(dbQuote as QuoteWithRelations));

      return {
        success: true,
        data: quotes,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching quotes by user'),
      };
    }
  }

  /**
   * Get quotes by client
   */
  async findByClient(
    userId: string,
    clientId: string,
    options?: PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Quote>>> {
    try {
      const db = getDbClient();
      const { page, limit } = this.parsePagination(options);
      const { from, to } = this.getRange(page, limit);

      const { data, error, count } = await db
        .from(this.tableName)
        .select(
          `
          *,
          client:clients(id, name, email),
          quote_items(
            id,
            service:services(id, name, rate),
            quantity,
            rate,
            total,
            description
          )
        `,
          { count: 'exact' }
        )
        .eq('user_id', userId)
        .eq('client_id', clientId)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) {
        throw error;
      }

      const quotes = (data || []).map((dbQuote) => this.mapToQuote(dbQuote as QuoteWithRelations));

      const pagination = this.buildPagination(page, limit, count || 0);

      return {
        success: true,
        data: {
          data: quotes,
          pagination,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching quotes by client'),
      };
    }
  }

  /**
   * Get quotes by status
   */
  async findByStatus(
    userId: string,
    status: Quote['status'],
    options?: PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Quote>>> {
    try {
      const db = getDbClient();
      const { page, limit } = this.parsePagination(options);
      const { from, to } = this.getRange(page, limit);

      const { data, error, count } = await db
        .from(this.tableName)
        .select(
          `
          *,
          client:clients(id, name, email),
          quote_items(
            id,
            service:services(id, name, rate),
            quantity,
            rate,
            total,
            description
          )
        `,
          { count: 'exact' }
        )
        .eq('user_id', userId)
        .eq('status', status)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) {
        throw error;
      }

      const quotes = (data || []).map((dbQuote) => this.mapToQuote(dbQuote as QuoteWithRelations));

      const pagination = this.buildPagination(page, limit, count || 0);

      return {
        success: true,
        data: {
          data: quotes,
          pagination,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching quotes by status'),
      };
    }
  }

  /**
   * Find quote by public token (for client portal)
   */
  async findByPublicToken(token: string): Promise<RepositoryResult<Quote>> {
    try {
      const db = getDbClient();

      const { data, error } = await db
        .from(this.tableName)
        .select(
          `
          *,
          client:clients(id, name, email),
          quote_items(
            id,
            service:services(id, name, rate),
            quantity,
            rate,
            total,
            description
          )
        `
        )
        .eq('public_token', token)
        .single();

      if (error) {
        throw error;
      }

      if (!data) {
        return {
          success: false,
          error: 'Quote not found',
        };
      }

      const quote = this.mapToQuote(data as QuoteWithRelations);

      return {
        success: true,
        data: quote,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching quote by token'),
      };
    }
  }

  /**
   * Check if a quote has dependencies (invoices created from it)
   */
  async hasDependencies(id: string): Promise<RepositoryResult<boolean>> {
    try {
      const db = getDbClient();

      // Check if quote has been converted to invoice
      const { count } = await db
        .from('invoices')
        .select('id', { count: 'exact', head: true })
        .eq('quote_id', id);

      return {
        success: true,
        data: (count || 0) > 0,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'checking quote dependencies'),
      };
    }
  }

  /**
   * Map database quote with relations to application Quote
   */
  protected mapToQuote(dbQuote: QuoteWithRelations): Quote {
    const client: Client = {
      id: dbQuote.client?.id || '',
      userId: dbQuote.user_id,
      name: dbQuote.client?.name || '',
      email: dbQuote.client?.email || '',
      phone: '',
    };

    const items: QuoteItem[] = (dbQuote.quote_items || []).map((qi) => ({
      id: qi.id,
      serviceId: qi.service?.id,
      description: qi.description || qi.service?.name || '',
      rate: qi.rate,
      quantity: qi.quantity,
      name: qi.service?.name,
      unitPrice: qi.total || qi.rate * qi.quantity,
    }));

    // Build quote object with proper optional handling
    const quote: Quote = {
      id: dbQuote.id,
      userId: dbQuote.user_id,
      clientId: dbQuote.client_id,
      title: dbQuote.title,
      description: dbQuote.description || '',
      status: dbQuote.status,
      currency: 'USD' as CurrencyCode,
      subtotal: dbQuote.subtotal || dbQuote.total_amount,
      taxRate: dbQuote.tax_rate || 0,
      taxAmount: dbQuote.tax_amount || 0,
      totalAmount: dbQuote.total_amount,
      validUntil: dbQuote.valid_until || '',
      createdAt: dbQuote.created_at,
      updatedAt: dbQuote.updated_at || dbQuote.created_at,
      quoteNumber: dbQuote.quote_number,
      items,
    };

    // Conditionally add optional properties
    if (dbQuote.notes) {
      quote.notes = dbQuote.notes;
    }
    if (dbQuote.terms) {
      quote.terms = dbQuote.terms;
    }
    if (dbQuote.public_token) {
      quote.publicToken = dbQuote.public_token;
    }

    return quote;
  }
}

// Export singleton instance
export const quoteQueryRepository = new QuoteQueryRepository();
